import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, Gift, Coins } from "@phosphor-icons/react";

const GIFT_EMOJI = { rose: "🌹", heart: "❤️", crown: "👑", rocket: "🚀", diamond: "💎" };

/**
 * Group chat over a live stream.
 *  - Polls /api/live/:id/chat every 3s with `since` cursor
 *  - User can post messages (max 500 chars)
 *  - Gift messages (kind=gift) render as a styled bubble
 */
export default function LiveChat({ liveId, currentUser, onOpenGift, onGiftMessage }) {
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const sinceRef = useRef(null);
    const listRef = useRef(null);
    const giftCbRef = useRef(onGiftMessage);
    giftCbRef.current = onGiftMessage;

    const append = useCallback((arr) => {
        if (!arr || arr.length === 0) return;
        const giftBuffer = [];
        setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const next = [...prev];
            for (const m of arr) {
                if (!seen.has(m.id)) {
                    next.push(m);
                    sinceRef.current = m.created_at;
                    if (m.kind === "gift") giftBuffer.push(m);
                }
            }
            // Keep last 200 in memory
            return next.slice(-200);
        });
        // Fire gift animations AFTER state commit (defer with microtask) to avoid
        // "Cannot update a component while rendering a different component" warning.
        if (giftBuffer.length && giftCbRef.current) {
            queueMicrotask(() => {
                for (const m of giftBuffer) {
                    try { giftCbRef.current(m); } catch { /* silent */ }
                }
            });
        }
    }, []);

    // Initial load
    useEffect(() => {
        if (!liveId) return;
        let cancelled = false;
        api.get(`/live/${liveId}/chat?limit=50`)
            .then(({ data }) => {
                if (cancelled || !Array.isArray(data)) return;
                if (data.length > 0) sinceRef.current = data[data.length - 1].created_at;
                setMessages(data);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [liveId]);

    // Poll new messages
    useEffect(() => {
        if (!liveId) return;
        const poll = async () => {
            try {
                const url = sinceRef.current
                    ? `/live/${liveId}/chat?since=${encodeURIComponent(sinceRef.current)}&limit=50`
                    : `/live/${liveId}/chat?limit=50`;
                const { data } = await api.get(url);
                if (Array.isArray(data) && data.length > 0) append(data);
            } catch { /* silent */ }
        };
        const t = setInterval(poll, 3000);
        return () => clearInterval(t);
    }, [liveId, append]);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault?.();
        const trimmed = text.trim();
        if (!trimmed) return;
        if (trimmed.length > 500) return toast.error("Max 500 characters");
        setSending(true);
        try {
            const { data } = await api.post(`/live/${liveId}/chat`, { text: trimmed });
            append([data]);
            setText("");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="live-chat" className="absolute left-3 right-3 bottom-24 sm:left-3 sm:right-auto sm:w-72 z-30 flex flex-col gap-2 pointer-events-none">
            {/* Messages */}
            <div
                ref={listRef}
                data-testid="live-chat-list"
                className="max-h-[40vh] overflow-y-auto pointer-events-auto space-y-1.5 pr-1"
                style={{ scrollbarWidth: "thin" }}
            >
                {messages.length === 0 ? (
                    <div className="text-[10px] text-white/50 italic">Be the first to say hi.</div>
                ) : messages.map((m) => {
                    const u = m.user || {};
                    const isMe = currentUser && u.id === currentUser.id;
                    if (m.kind === "gift") {
                        const emoji = m.gift_emoji || GIFT_EMOJI[m.gift_type] || "🎁";
                        const name = m.gift_name || m.gift_type;
                        const color = m.gift_color || "#F59E0B";
                        return (
                            <div key={m.id} data-testid={`live-chat-msg-${m.id}`} className="flex items-start gap-1.5 animate-fade-in">
                                <div
                                    className="flex-1 px-2 py-1 rounded-sm border text-xs"
                                    style={{
                                        background: `linear-gradient(90deg, ${color}33, ${color}14)`,
                                        borderColor: `${color}66`,
                                    }}
                                >
                                    <span className="font-heading" style={{ color }}>@{u.name || "someone"}</span>
                                    <span className="text-white/80"> sent </span>
                                    {m.gift_image_url ? (
                                        <img src={m.gift_image_url} alt="" className="inline-block w-4 h-4 object-contain align-text-bottom" />
                                    ) : (
                                        <span>{emoji}</span>
                                    )}
                                    <span className="ml-1 font-heading uppercase tracking-wider text-[10px]" style={{ color }}>{name}</span>
                                    <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-wta-accent">
                                        <Coins size={10} weight="fill" />{m.gift_value}
                                    </span>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={m.id} data-testid={`live-chat-msg-${m.id}`} className="flex items-start gap-1.5 animate-fade-in">
                            <div className={`px-2 py-1 rounded-sm text-xs leading-snug max-w-full ${isMe ? "bg-wta-primary/30 border border-wta-primary/40" : "bg-black/55 backdrop-blur-sm"}`}>
                                <span className={`font-heading text-[11px] mr-1 ${isMe ? "text-wta-primary" : "text-wta-accent"}`}>@{u.name || "user"}</span>
                                <span className="text-white whitespace-pre-wrap break-words">{m.text}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Input */}
            {currentUser && (
                <form
                    onSubmit={send}
                    onClick={(e) => e.stopPropagation()}
                    className="pointer-events-auto flex items-center gap-1.5 bg-black/65 backdrop-blur-md border border-white/15 rounded-full pl-3 pr-1 py-1"
                >
                    <input
                        data-testid="live-chat-input"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        maxLength={500}
                        placeholder="Say something…"
                        className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/50 min-w-0"
                    />
                    {onOpenGift && (
                        <button
                            type="button"
                            data-testid="live-chat-gift-btn"
                            onClick={(e) => { e.stopPropagation(); onOpenGift(); }}
                            className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-wta-accent/20 text-wta-accent hover:bg-wta-accent/30"
                            aria-label="Send gift"
                        >
                            <Gift size={16} weight="fill" />
                        </button>
                    )}
                    <button
                        type="submit"
                        data-testid="live-chat-send"
                        disabled={sending || !text.trim()}
                        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-wta-primary text-white disabled:opacity-40"
                        aria-label="Send message"
                    >
                        <PaperPlaneRight size={14} weight="fill" />
                    </button>
                </form>
            )}
        </div>
    );
}
